import { CHAT_SEARCH_MIN_QUERY_LENGTH } from "@frontend-shared/constants";
import type {
  IChatListObject,
  ICrossChatSearchHit,
} from "@frontend-shared/types";
import {
  getHasOrgWithSearchEmbeddingModel,
  hasSemanticSection as resolveHasSemanticSection,
  resolveSemanticSearchHits,
} from "@frontend-shared/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";

import { useChatList } from "@/api/chat/useChatList";
import { useSemanticChatSearch } from "@/api/chat/useSemanticChatSearch";
import { PATHS } from "@/constants/paths";
import { useAuth } from "@/hooks/use-auth";
import { useOnPremise } from "@/hooks/use-on-premise";

import type {
  IGlobalSearchChatsState,
  IUseGlobalSearchChatsProps,
} from "../types";

// NOTE: The chat half of the palette, moved as-is from the old SearchChats
// dialog: `useChatList` runs the server-side keyword search over chat titles
// and message content (NSAI-4334), `useSemanticChatSearch` the cross-chat
// vector search. NSAI-6589 changed one thing — the palette is available to
// everyone now, so only the semantic half keeps the on-premise gate: without a
// search embedding model there is nothing to embed the query with.
export const useGlobalSearchChats = ({
  query,
  debouncedQuery,
  onClose,
}: IUseGlobalSearchChatsProps): IGlobalSearchChatsState => {
  const navigate = useNavigate();
  const { isOnPremise } = useOnPremise();
  const { projects, currentProjectUuid, onCurrentProjectChange } = useAuth();
  const isSemanticAvailable =
    !isOnPremise || getHasOrgWithSearchEmbeddingModel(projects);
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(
    null,
  );
  const { ref: loadMoreRef, inView: isInView } = useInView({
    root: scrollContainer,
    skip: !scrollContainer,
  });

  const {
    data: titleData,
    isLoading: isTitleQueryLoading,
    isError: isTitleError,
    refetch: refetchTitle,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useChatList({ search: debouncedQuery.trim() });
  const {
    data: semanticData,
    isLoading: isSemanticQueryLoading,
    isError: isSemanticError,
    refetch: refetchSemantic,
  } = useSemanticChatSearch({
    query: debouncedQuery,
    enabled: isSemanticAvailable,
  });

  useEffect(() => {
    if (isInView && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [isInView, hasNextPage, fetchNextPage, isFetchingNextPage]);

  const onSelectChat = useCallback(
    (chat: IChatListObject): void => {
      navigate(`${PATHS.CHAT}/${chat.uuid}`);
      onClose();
    },
    [navigate, onClose],
  );
  // NOTE: NSAI-6720 — semantic search spans every project of the organization
  // and a chat is only readable inside its own, so a hit from another project
  // switches to it first. onCurrentProjectChange queues its own
  // navigate(/chat); the later navigate below wins.
  const onSelectHit = useCallback(
    (hit: ICrossChatSearchHit): void => {
      if (hit.projectUuid && hit.projectUuid !== currentProjectUuid) {
        onCurrentProjectChange(hit.projectUuid);
      }
      navigate(`${PATHS.CHAT}/${hit.chatUuid}`);
      onClose();
    },
    [currentProjectUuid, navigate, onClose, onCurrentProjectChange],
  );

  // NOTE: NSAI-4336 — retries only the half that actually failed, so a
  // semantic query that succeeded does not pay for another embedding.
  const onRetry = useCallback((): void => {
    if (isTitleError) void refetchTitle();
    if (isSemanticError) void refetchSemantic();
  }, [isTitleError, isSemanticError, refetchTitle, refetchSemantic]);

  const isDebouncing = query !== debouncedQuery;
  const trimmedQuery = query.trim();
  // NOTE: NSAI-4334 — from the live input so the hint appears on the
  // keystroke; an empty box is browse mode, not a too-short search.
  const isQueryBelowMinLength =
    trimmedQuery.length > 0 &&
    trimmedQuery.length < CHAT_SEARCH_MIN_QUERY_LENGTH;
  const isSemanticLoading =
    isSemanticAvailable && (isSemanticQueryLoading || isDebouncing);

  const chats = useMemo(
    () => titleData?.pages.flatMap((page) => page.data) ?? [],
    [titleData],
  );
  const semanticHits = useMemo(
    () =>
      isSemanticAvailable
        ? resolveSemanticSearchHits({
            hits: semanticData,
            keywordChatUuids: chats.map((chat) => chat.uuid),
            debouncedQuery,
            isQueryBelowMinLength,
          })
        : null,
    [
      isSemanticAvailable,
      semanticData,
      chats,
      debouncedQuery,
      isQueryBelowMinLength,
    ],
  );

  return {
    chats,
    semanticHits,
    isTitleLoading: isTitleQueryLoading || isDebouncing,
    isTitleError,
    isSemanticLoading,
    isSemanticError,
    hasSemanticSection: resolveHasSemanticSection({
      hits: semanticHits,
      isLoading: isSemanticLoading,
      isError: isSemanticError,
    }),
    isQueryBelowMinLength,
    isFetchingNextPage,
    loadMoreRef,
    setScrollContainer,
    onRetry,
    onSelectChat,
    onSelectHit,
  };
};
